import { Injectable, signal } from "@angular/core";
import {
  isStorageAvailable,
  storageGet,
  storageRemove,
  storageSet,
} from "../utils/safe-storage";

@Injectable({
  providedIn: "root",
})
export class AuthTokenService {
  private readonly STORAGE_KEY = "auth_token";
  /** Single read at construction: it seeds BOTH the signal and the memory
   *  fallback, so the two can never start out disagreeing. */
  private readonly initialToken = storageGet(this.STORAGE_KEY);
  readonly token = signal<string | null>(this.initialToken);
  /**
   * In-memory fallback for documents where Web Storage is blocked (#622).
   * A plain field on purpose, NOT a signal: getToken() must stay non-tracking
   * (see the comment there). The session then lives only until reload, which is
   * the best a browser without storage can offer — but the app boots and login
   * works instead of dying at bootstrap.
   *
   * Seeded from the construction-time read because storage can also trip
   * MID-session (blocked mid-flight, quota): with an empty fallback, a token
   * carried over from a previous session would vanish from getToken() while the
   * signal-driven isAuthenticated() still reported "logged in", and the
   * interceptor would quietly stop sending Authorization.
   */
  private memoryToken: string | null = this.initialToken;

  setToken(token: string) {
    // Memory + signal are updated unconditionally, so a storage write that
    // throws cannot cost us the session.
    this.memoryToken = token;
    storageSet(this.STORAGE_KEY, token);
    this.token.set(token);
  }

  clearToken() {
    this.memoryToken = null;
    storageRemove(this.STORAGE_KEY);
    this.token.set(null);
  }

  getToken(): string | null {
    // Read from localStorage directly to avoid signal tracking.
    // This is critical: if called inside a reactive context (e.g., rxResource's
    // stream via an HTTP interceptor), reading this.token() would make the token
    // a dependency, causing resources to re-fire on every token change.
    // The memory fallback is a plain field, so it is non-tracking as well.
    const fromStorage = storageGet(this.STORAGE_KEY);
    return isStorageAvailable() ? fromStorage : this.memoryToken;
  }
}
